/**
 * Template auto-selection for sidebar dictation.
 *
 * Contract (product):
 * - Evaluate ONLY the first N words of the *current* template-matching window
 *   (default N = 10). Never use a rolling window within that segment.
 * - Once those first N words are captured, they stay frozen until the user
 *   explicitly clears the transcription or removes the selected template.
 * - Words after N in the active window are always normal dictation and never
 *   feed template matching.
 * - Clear / remove starts a *fresh* template-matching window on the same
 *   mic/socket session: discard the previous frozen buffer and do NOT replay
 *   old speech. Only newly spoken words form the next first-N window.
 * - Soft-suppress auto-apply after a successful selection until clear/remove
 *   (not a permanent lockout).
 *
 * STT display modes (see getSidebarDictationSttMode):
 * - incremental (en, es, …): window source grows from sidebar (+ pending delta),
 *   minus any ignore-prefix left after a template-remove reset.
 * - soniox (he, fr, ar): window source is anchored Soniox finals cumulative
 *   (sessionText). Prefer server `transcriptCumulative` when present; otherwise
 *   the client appends each final delta (backend historically omits cumulative).
 */

import { logSidebarTemplate, previewText } from "./sidebarTemplateSelectionDebug";

export const DEFAULT_TEMPLATE_SELECTION_MAX_WORDS = 10;

export const STT_MODE = {
  INCREMENTAL: "incremental",
  SONIOX: "soniox",
};

export function countWords(text) {
  const normalized = String(text ?? "").trim();
  if (!normalized) return 0;
  return normalized.split(/\s+/).filter(Boolean).length;
}

export function takeFirstWords(text, maxWords) {
  const words = String(text ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length || maxWords <= 0) return "";
  return words.slice(0, maxWords).join(" ");
}

function sliceAfterAnchor(fullCumulative, anchor) {
  const cumulative = String(fullCumulative ?? "");
  const anchorText = String(anchor ?? "");
  if (!anchorText) return cumulative;
  if (cumulative.startsWith(anchorText)) {
    return cumulative.slice(anchorText.length).replace(/^\s+/, "");
  }
  return cumulative;
}

export class SidebarTemplateSelectionSession {
  constructor({ maxWords = DEFAULT_TEMPLATE_SELECTION_MAX_WORDS } = {}) {
    this.maxWords = maxWords;
    this.sttMode = STT_MODE.INCREMENTAL;
    /** @type {string} Dictation source for the *current* template-matching window. */
    this.sessionDictationText = "";
    /** @type {string|null} Frozen first-N words for the current window; null until locked. */
    this.frozenFirstWords = null;
    /** Once true, first-N words never change until clear/remove resets the window. */
    this.firstWordsLocked = false;
    /**
     * Soft suppress after a successful auto-apply. Cleared when transcript is
     * cleared or the selected template is removed — never a permanent lockout.
     */
    this.autoSelectEnabled = true;
    /**
     * Incremental mode: sidebar text present at template-remove reset. New
     * matching must ignore this prefix so leftover "CT Elbow …" is not replayed.
     */
    this.templateWindowIgnorePrefix = "";
    this.sidebarText = "";
    this.rawCumulative = "";
    this.anchor = "";
    this.sessionText = "";
    this.pendingPostClearBaseline = false;
  }

  resetForMicSession({ sttMode = STT_MODE.INCREMENTAL } = {}) {
    this.sttMode = sttMode;
    this._resetTemplateMatchingBuffer({ source: "mic-session-start" });
    this.sidebarText = "";
    this.rawCumulative = "";
    this.anchor = "";
    this.sessionText = "";
    this.pendingPostClearBaseline = false;
    this.templateWindowIgnorePrefix = "";
    logSidebarTemplate("mic-session-start", { sttMode, maxWords: this.maxWords });
  }

  /**
   * User cleared the transcription UI while the mic/socket session may still
   * be active. Starts a fresh template-matching window (old buffer discarded).
   */
  onTranscriptCleared() {
    this._resetTemplateMatchingBuffer({ source: "transcript-cleared" });
    this.templateWindowIgnorePrefix = "";
    // Client-built Soniox finals restart from an empty cumulative. No baseline
    // anchor needed — the next final delta is the start of the new window.
    this.pendingPostClearBaseline = false;
    this.rawCumulative = "";
    this.anchor = "";
    this.sessionText = "";
    this.sidebarText = "";
    logSidebarTemplate("transcript-cleared", {
      sttMode: this.sttMode,
      awaitingPostClearBaseline: false,
      autoSelectEnabled: this.autoSelectEnabled,
      firstWordsLocked: this.firstWordsLocked,
    });
  }

  /**
   * User removed/deselected the applied template without ending the mic session.
   * Starts a fresh template-matching window. Leftover sidebar/cumulative text
   * from before the remove must NOT be re-evaluated.
   */
  onTemplateSelectionCleared() {
    const previousMatch = this.getOriginalFirstWords();
    this._resetTemplateMatchingBuffer({ source: "template-selection-cleared" });

    if (this.sttMode === STT_MODE.SONIOX) {
      if (this.rawCumulative) {
        this.anchor = this.rawCumulative;
        this.sessionText = "";
        this.pendingPostClearBaseline = false;
      } else {
        // No finals yet — next delta starts a fresh window (no baseline trap).
        this.pendingPostClearBaseline = false;
        this.anchor = "";
        this.sessionText = "";
      }
      this.templateWindowIgnorePrefix = "";
    } else {
      // Ignore existing sidebar content; only newly spoken/appended text counts.
      this.templateWindowIgnorePrefix = String(this.sidebarText ?? "").trim();
    }

    logSidebarTemplate("template-selection-cleared", {
      sttMode: this.sttMode,
      discardedMatchPreview: previewText(previousMatch),
      ignorePrefixPreview: previewText(this.templateWindowIgnorePrefix),
      autoSelectEnabled: true,
      firstWordsLocked: false,
    });
  }

  updateSidebarText(text) {
    this.sidebarText = String(text ?? "");
    if (this.sttMode === STT_MODE.INCREMENTAL && !this.pendingPostClearBaseline) {
      this._ingestSessionDictationSource(
        this._incrementalSourceAfterIgnore(this.sidebarText, ""),
      );
    }
  }

  ingestRawCumulative(rawCumulative) {
    const raw = String(rawCumulative ?? "");
    if (!raw) {
      return this.getSonioxSnapshot();
    }

    this.rawCumulative = raw;

    if (this.pendingPostClearBaseline) {
      this.anchor = raw;
      this.pendingPostClearBaseline = false;
      this.sessionText = "";
      logSidebarTemplate("post-clear-baseline", {
        anchorLength: this.anchor.length,
        anchorPreview: previewText(this.anchor),
      });
    } else {
      this.sessionText = sliceAfterAnchor(raw, this.anchor);
      if (this.sttMode === STT_MODE.SONIOX) {
        this._ingestSessionDictationSource(this.sessionText);
      }
    }

    logSidebarTemplate("cumulative-ingested", {
      sttMode: this.sttMode,
      rawLength: raw.length,
      sessionWordCount: countWords(this.sessionText),
      sessionPreview: previewText(this.sessionText),
      frozenFirstWords: previewText(this.getOriginalFirstWords()),
      firstWordsLocked: this.firstWordsLocked,
      anchorLength: this.anchor.length,
    });

    return this.getSonioxSnapshot();
  }

  /**
   * Append one Soniox final delta into the client-built finals cumulative.
   * Mirrors Soniox token concatenation (no inserted spaces) and feeds the same
   * path as server `transcriptCumulative` when the backend omits that field.
   *
   * @param {string} delta
   */
  appendSonioxFinalDelta(delta) {
    const chunk = String(delta ?? "");
    if (!chunk) {
      return this.getSonioxSnapshot();
    }
    const nextRaw = `${this.rawCumulative || ""}${chunk}`;
    logSidebarTemplate("soniox-final-delta-appended", {
      deltaPreview: previewText(chunk),
      nextRawLength: nextRaw.length,
      hadServerCumulative: false,
    });
    return this.ingestRawCumulative(nextRaw);
  }

  /** True when template-remove re-anchored; display must append, not full-replace. */
  hasSonioxMatchAnchor() {
    return Boolean(this.anchor);
  }

  /**
   * First-N words for the *current* template-matching window.
   * @returns {string}
   */
  getOriginalFirstWords() {
    if (this.frozenFirstWords != null) {
      return this.frozenFirstWords;
    }
    return takeFirstWords(this.sessionDictationText, this.maxWords);
  }

  /**
   * @param {{
   *   commandMode: boolean,
   *   sidebarText?: string,
   *   sessionText?: string,
   *   pendingDelta?: string,
   * }} options
   */
  getVoiceTemplateGate({
    commandMode,
    sidebarText,
    sessionText,
    pendingDelta = "",
  }) {
    const visible = String(sidebarText ?? this.sidebarText);
    const session = String(sessionText ?? this.sessionText);
    const delta = String(pendingDelta ?? "").trim();

    if (!this.firstWordsLocked) {
      if (this.sttMode === STT_MODE.SONIOX) {
        this._ingestSessionDictationSource(session);
      } else {
        this._ingestSessionDictationSource(
          this._incrementalSourceAfterIgnore(visible, delta),
        );
      }
    }

    const templateMatchText = this.getOriginalFirstWords();
    const hasMatchText = countWords(templateMatchText) > 0;
    const allowedInDictation = hasMatchText && this.autoSelectEnabled;

    const gate = {
      sttMode: this.sttMode,
      maxWords: this.maxWords,
      sessionWordCount: countWords(session),
      sidebarWordCount: countWords(visible),
      sessionDictationWordCount: countWords(this.sessionDictationText),
      firstWordsLocked: this.firstWordsLocked,
      frozenFirstWords: this.frozenFirstWords,
      autoSelectEnabled: this.autoSelectEnabled,
      templateWindowIgnorePrefix: this.templateWindowIgnorePrefix,
      /** @deprecated kept for log compatibility — never a hard post-select lockout */
      lockedOut: !this.autoSelectEnabled,
      allowTemplateCommands: commandMode || allowedInDictation,
      allowTriggerlessTemplate: commandMode ? false : allowedInDictation,
      /** First-N of the current window only — never words N+1 of that window. */
      templateMatchText: hasMatchText ? templateMatchText : "",
    };

    logSidebarTemplate("template-gate", {
      commandMode,
      ...gate,
      gatingSource: this.sttMode === STT_MODE.SONIOX ? "session" : "sidebar",
      sidebarPreview: previewText(visible),
      sessionPreview: previewText(session),
      deltaPreview: previewText(delta),
      matchPreview: previewText(gate.templateMatchText),
    });

    return gate;
  }

  /**
   * Soft-suppress further auto-apply until clear/remove. Does NOT clear the
   * current window (clear/remove does that explicitly).
   */
  lockAfterTemplateSelected() {
    this.autoSelectEnabled = false;
    logSidebarTemplate("auto-select-suppressed", {
      source: "template-selected",
      sttMode: this.sttMode,
      autoSelectEnabled: false,
      frozenFirstWords: previewText(this.getOriginalFirstWords()),
      firstWordsLocked: this.firstWordsLocked,
      maxWords: this.maxWords,
    });
  }

  /** @deprecated No word-count lockout; kept as no-op for call-site compatibility. */
  finalizeAfterTemplateAttempt() {
    // Intentionally empty: first-N freeze is handled during ingest/gate only.
  }

  getSonioxSnapshot() {
    return {
      sessionText: this.sessionText,
      sessionWordCount: countWords(this.sessionText),
      originalFirstWords: this.getOriginalFirstWords(),
      firstWordsLocked: this.firstWordsLocked,
    };
  }

  _resetTemplateMatchingBuffer({ source } = {}) {
    this.sessionDictationText = "";
    this.frozenFirstWords = null;
    this.firstWordsLocked = false;
    this.autoSelectEnabled = true;
    logSidebarTemplate("template-matching-buffer-reset", {
      source,
      sttMode: this.sttMode,
      maxWords: this.maxWords,
    });
  }

  /**
   * Strip sidebar text that existed at template-remove time so leftover
   * dictation (e.g. "CT Elbow …") is never replayed into a new window.
   * Prefer sidebar content when present; only append delta when it is not
   * already reflected in that content (avoids duplicating the same final).
   */
  _incrementalSourceAfterIgnore(visible, delta = "") {
    const ignore = String(this.templateWindowIgnorePrefix ?? "").trim();
    const vis = String(visible ?? "").trim();
    const d = String(delta ?? "").trim();

    let fromVisible = vis;
    if (ignore) {
      if (!vis || vis === ignore) {
        fromVisible = "";
      } else if (vis.startsWith(ignore)) {
        fromVisible = vis.slice(ignore.length).replace(/^\s+/, "");
      } else {
        // User replaced/cleared the old sidebar content — treat full text as new.
        fromVisible = vis;
      }
    }

    if (fromVisible) {
      if (
        d &&
        !fromVisible.endsWith(d) &&
        !fromVisible.endsWith(` ${d}`) &&
        fromVisible !== d
      ) {
        return `${fromVisible} ${d}`.trim();
      }
      return fromVisible;
    }
    return d;
  }

  _ingestSessionDictationSource(sourceText) {
    if (this.firstWordsLocked) return;
    const next = String(sourceText ?? "").trim();
    if (!next) {
      this.sessionDictationText = "";
      return;
    }

    // Always take the prefix from the start of the current window source —
    // never a rolling window of the latest N words.
    this.sessionDictationText = next;
    const prefix = takeFirstWords(next, this.maxWords);
    if (countWords(next) >= this.maxWords) {
      this.frozenFirstWords = prefix;
      this.firstWordsLocked = true;
      logSidebarTemplate("first-words-frozen", {
        maxWords: this.maxWords,
        frozenFirstWords: previewText(this.frozenFirstWords),
      });
    }
  }
}

export function createSidebarTemplateSelectionSession(options) {
  return new SidebarTemplateSelectionSession(options);
}
